import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Shield, ChevronRight, MessageCircle } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BoundariesPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(null);

  useEffect(() => {
    if (hasAccess) {
      fetchTopics();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  const fetchTopics = async () => {
    try {
      const response = await axios.get(`${API}/boundaries`);
      setTopics(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta innehåll');
    } finally {
      setLoading(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/relations')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Startsida
            </Button>
            <h1 className="font-heading text-xl font-light">Gränser</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Gränser" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="boundaries-page">
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedTopic ? setSelectedTopic(null) : navigate('/category/relations')}
            className="rounded-full hover:bg-secondary/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedTopic ? 'Tillbaka' : 'Startsida'}
          </Button>
          <h1 className="font-heading text-xl font-light">Gränser</h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
          </div>
        ) : selectedTopic ? (
          <div className="animate-fade-in space-y-8">
            <div className="bg-teal-100/50 rounded-2xl p-8">
              <Shield className="w-10 h-10 text-primary mb-4" />
              <h2 className="font-heading text-3xl font-light mb-2">{selectedTopic.title}</h2>
              <p className="text-muted-foreground">{selectedTopic.description}</p>
            </div>

            <div className="bg-card rounded-2xl p-8 space-y-4">
              {selectedTopic.content.map((text, i) => (
                <p key={i} className="text-foreground" dangerouslySetInnerHTML={{ __html: text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
              ))}
            </div>

            <div className="bg-secondary/30 rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-xl">Exempel</h3>
              <ul className="space-y-2">
                {selectedTopic.examples.map((ex, i) => (
                  <li key={i} className="flex gap-3 items-start">
                    <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                    <span className="text-muted-foreground">{ex}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-primary/5 rounded-2xl p-8 space-y-4">
              <h3 className="font-heading text-xl">Affirmationer</h3>
              {selectedTopic.affirmations.map((aff, i) => (
                <p key={i} className="text-lg font-light text-center py-2 border-b border-border/30 last:border-0 italic">"{aff}"</p>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="text-center mb-10 space-y-3 animate-fade-in">
              <div className="w-16 h-16 mx-auto rounded-full bg-teal-100/50 flex items-center justify-center">
                <Shield className="w-7 h-7 text-primary" />
              </div>
              <h2 className="font-heading text-3xl font-light">Gränser</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Lär dig sätta hälsosamma gränser utan skuld eller ångest.
              </p>
            </div>

            <div className="space-y-4">
              {topics.map((topic, index) => (
                <button
                  key={topic.id}
                  onClick={() => setSelectedTopic(topic)}
                  className="w-full bg-teal-100/50 p-6 rounded-2xl text-left hover:shadow-soft transition-shadow opacity-0 animate-slide-up group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-card flex items-center justify-center">
                        <Shield className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl">{topic.title}</h3>
                        <p className="text-sm text-muted-foreground">{topic.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
